import Delta from 'quill-delta';
import { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useLocalizedState, useViewerRoles } from '@treams/treams-hooks';
import {
	LocalizedAttributedString,
	PageType,
	usePageQuery,
	useUpdatePageMutation,
} from '@treams/treams-network';

import { Breakpoint } from '../helpers';
import { ArrowLeftIcon, PencilEditBoxIcon } from '../icons';
import { PAGE_CONTENT_WIDTH, space, tokens } from '../theme/tokens';
import { Container } from './Container';
import { Editable } from './Editable';
import { Paragraph, Title } from './Text';
import welcomeSwirl from './assets/jumbo-welcome-swirl.svg?url';
import { Button } from './button/Button';
import { IconButton } from './button/IconButton';

const Background = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;
`;

const WELCOME_INSET = 20;

const WelcomeFrame = styled.div`
	padding: ${WELCOME_INSET}px ${WELCOME_INSET}px 37px;
`;

const WelcomeBanner = styled.div`
	position: relative;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	width: 100%;
	padding: 80px 0;
	background: ${tokens.surface.sidebar};
	border-radius: ${tokens.radius.lg}px;
`;

const WelcomeBleed = styled.div`
	margin: 0 -${WELCOME_INSET}px;
`;

const WelcomeColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${tokens.gap.list}px;
	width: 100%;
	text-align: left;
`;

const WelcomeSwirl = styled.img`
	position: absolute;
	top: calc(50% + 11px);
	right: 32px;
	width: 349px;
	height: 330px;
	transform: translateY(-50%);
	pointer-events: none;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		display: none;
	}
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: ${tokens.gap.list}px;
	margin-top: ${tokens.gap.section}px;
`;

const WelcomeTitle = styled(Title)`
	position: relative;
	z-index: 1;
	width: 100%;
	color: ${tokens.text.white};
`;

const WelcomeCopy = styled.div`
	display: flex;
	flex: 1 1 auto;
	align-items: flex-start;
	gap: ${tokens.gap.list}px;
	max-width: 640px;
	width: 100%;
	position: relative;
	z-index: 1;
	text-align: left;

	${Paragraph},
	.ql-readonly {
		flex: 1 1 auto;
		min-width: 0;
	}

	${Paragraph},
	.ql-readonly,
	.ql-readonly p,
	.ql-readonly li {
		color: ${tokens.text.tertiary} !important;
	}
`;

type Props = PropsWithChildren<{
	title?: string;
	alternativePageTitle?: string;
	description?: string;
	header?: ReactNode;
	button?: ReactNode;
	/** Places `button` on the title row, aligned to the far right. */
	inlineButton?: boolean;
	footer?: ReactNode;
	backButtonLink?: string;
	alignItems?: 'center' | 'flex-start';
	wide?: number | boolean;
	variant?: 'default' | 'welcome';
}>;

const JumboContainer: FC<Props> = ({
	title,
	alternativePageTitle,
	header,
	footer,
	button,
	inlineButton,
	backButtonLink,
	wide,
	variant,
	children,
}) => {
	const { t } = useTranslation();
	const welcome = variant === 'welcome';
	const welcomeWidth = typeof wide === 'number' ? wide : PAGE_CONTENT_WIDTH;

	const heading = title ? (
		welcome ? (
			<WelcomeTitle>{title}</WelcomeTitle>
		) : (
			<Title>{title}</Title>
		)
	) : (
		<Title.Skeleton />
	);

	const chrome = (
		<>
			{header && <Box mb={3}>{header}</Box>}
			{backButtonLink && (
				<Box mb={3} ml="-16px">
					<Flex>
						<Button
							icon={<ArrowLeftIcon />}
							title={t('jumbo.back.title')}
							link={backButtonLink}
							type="textIcon"
						/>
					</Flex>
				</Box>
			)}
		</>
	);

	return (
		<>
			<Helmet>
				<title>{alternativePageTitle ?? title}</title>
			</Helmet>
			<Background>
				<Container
					wide={welcome ? true : (wide ?? PAGE_CONTENT_WIDTH)}
					{...(welcome ? { px: 0 } : {})}
				>
					{welcome ? (
						<WelcomeFrame>
							<WelcomeBanner>
								<WelcomeSwirl src={welcomeSwirl} alt="" />
								<WelcomeBleed>
									<Container wide={welcomeWidth}>
										<WelcomeColumn>
											{chrome}
											{heading}
											{children}
											{button}
										</WelcomeColumn>
									</Container>
								</WelcomeBleed>
							</WelcomeBanner>
						</WelcomeFrame>
					) : (
						<Box pt={`${space['3xl']}px`} pb={4}>
							{chrome}
							{inlineButton && button ? (
								<Flex
									alignItems="center"
									justifyContent="space-between"
									style={{ gap: tokens.gap.list }}
								>
									<Box minWidth={0} flexGrow={1}>
										{heading}
										{children}
									</Box>
									<Box flexShrink={0}>{button}</Box>
								</Flex>
							) : (
								<>
									<Box
										maxWidth={children ? undefined : 640}
										width="100%"
										flexGrow={1}
										flexShrink={1}
									>
										{heading}
										{children}
									</Box>
									{button && <Actions>{button}</Actions>}
								</>
							)}
						</Box>
					)}
				</Container>
			</Background>
			{footer}
		</>
	);
};

export const Jumbo = (props: Props) => {
	const { description, children, variant } = props;
	return (
		<JumboContainer {...props}>
			{description &&
				(variant === 'welcome' ? (
					<WelcomeCopy>
						<Paragraph>{description}</Paragraph>
					</WelcomeCopy>
				) : (
					<Paragraph py={2}>{description}</Paragraph>
				))}
			{children}
		</JumboContainer>
	);
};

function JumboEditableContent(
	props: Props & {
		children: ReactNode;
		hasMediaUpload?: boolean;
		onFinishEditing: (value: LocalizedAttributedString) => void;
		pageContent?: LocalizedAttributedString;
	},
) {
	const [editing, setEditing] = useState(false);
	const roles = useViewerRoles();
	const { language } = useLocalize();
	const hasExistingTranslation = props.pageContent?.find(
		(content) => content.languageID === language.id,
	);
	const defaultDescription = {
		languageID: language.id,
		text: new Delta([{ insert: props.description || '' }]),
	};
	const content = hasExistingTranslation ?? defaultDescription;

	const isSuperUser = roles.isWorkspaceSuperuser;
	const [value, setValue] = useState([content]);
	const [localizedValue, setLocalizedValue] = useLocalizedState(value, setValue);
	const welcome = props.variant === 'welcome';

	useEffect(() => {
		// Refresh the value when the user navigates to a different page.
		setValue([content]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.pageContent, language.id]);

	const editButton = !isSuperUser ? null : (
		<IconButton type="secondary" icon={<PencilEditBoxIcon />} onClick={() => setEditing(true)} />
	);

	return (
		<JumboContainer {...props}>
			<Box mr={welcome ? 0 : 2} width={welcome ? '100%' : undefined}>
				<Editable.Quill
					active={editing}
					hasMediaUpload={props.hasMediaUpload}
					onDone={() => setEditing(false)}
					value={localizedValue}
					onChange={async (newValue) => {
						const localizedValue = await setLocalizedValue(newValue);
						props.onFinishEditing(localizedValue);
					}}
				>
					{(internalValue, { active }) =>
						welcome ? (
							<WelcomeCopy>
								{internalValue && <Paragraph>{internalValue}</Paragraph>}
								{!active && editButton}
							</WelcomeCopy>
						) : (
							<Flex py={2} alignItems="flex-start" style={{ gap: tokens.gap.group }}>
								{internalValue && (
									<Box flexGrow={1} flexShrink={1} minWidth={0}>
										<Paragraph>{internalValue}</Paragraph>
									</Box>
								)}
								{!active && editButton}
							</Flex>
						)
					}
				</Editable.Quill>
			</Box>
			{props.children}
		</JumboContainer>
	);
}

const JumboEditable: FC<Props & { page: PageType; hasMediaUpload?: boolean }> = (props) => {
	const { language } = useLocalize();
	const [updatePage] = useUpdatePageMutation();
	const { data, loading } = usePageQuery({
		variables: {
			type: props.page,
		},
	});

	const pageContent = data?.page?.content;

	const finishEditing = (content: LocalizedAttributedString) => {
		const currentContent = content.find((c) => c.languageID === language.id);

		if (currentContent) {
			updatePage({
				variables: {
					input: {
						content: currentContent.text,
						locale: language.id,
						type: props.page,
					},
				},
			});
		}
	};

	if (loading) {
		return (
			<JumboContainer {...props}>
				{props.variant === 'welcome' ? (
					<WelcomeCopy>
						<Paragraph.Skeleton my={2} opacity={0.4} lines={1} width="30%" />
					</WelcomeCopy>
				) : (
					<Paragraph.Skeleton my={2} opacity={0.4} lines={1} width="30%" />
				)}
				{props.children}
			</JumboContainer>
		);
	}

	return (
		<JumboEditableContent pageContent={pageContent} onFinishEditing={finishEditing} {...props}>
			{props.children}
		</JumboEditableContent>
	);
};

Jumbo.Editable = JumboEditable;

function JumboSkeleton(props: { wide?: number | boolean; children?: ReactNode }) {
	return (
		<Background>
			<Container wide={props.wide}>
				<Box maxWidth={640} flexGrow={1} flexShrink={1} pt={`${space['3xl']}px`} pb={4}>
					<Title.Skeleton />
					{props.children}
				</Box>
			</Container>
		</Background>
	);
}

Jumbo.Skeleton = JumboSkeleton;
